import React from 'react';
import { Link } from 'react-router-dom';
import about from '../images/about_org.jpg';
import { useDocTitle } from '../components/CustomHook';
import CommonContainer from '../components/CommonContainer';
import { commonStyles } from '../pages/commonStyles';

/* ------------------------------------------------------------------ */
/* Small building blocks                                               */
/* ------------------------------------------------------------------ */

const Pill = ({ tone = 'pick', children }) => (
  <span
    className={`ml-2 inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-bold tracking-wide ${
      tone === 'pick'
        ? 'bg-green-50 text-green-700'
        : 'bg-blue-50 text-blue-700'
    }`}
  >
    {children}
  </span>
);

const Note = ({ tone = 'info', title, children }) => (
  <div
    className={`mt-4 rounded-r-md border-l-4 py-3.5 px-5 text-[15px] leading-7 ${
      tone === 'warn'
        ? 'border-amber-500 bg-amber-50 text-amber-800'
        : 'border-blue-600 bg-blue-50 text-gray-600'
    }`}
  >
    {title && <strong className={tone === 'warn' ? 'text-amber-900' : 'text-blue-900'}>{title} </strong>}
    {children}
  </div>
);

const SpecTable = ({ columns, rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full border-collapse text-[14.5px]">
      <thead>
        <tr>
          {columns.map((c) => (
            <th
              key={c}
              className="border-b border-gray-200 py-2.5 pr-3 text-left text-[11.5px] font-bold uppercase tracking-wider text-gray-400"
            >
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td
                key={j}
                className={`border-b border-gray-200 py-3 pr-3 align-top ${
                  j === 0 ? 'whitespace-nowrap font-semibold text-blue-900' : 'text-gray-600'
                }`}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Section = ({ title, tag, lede, children }) => (
  <div className="mt-14">
    <h2 className="text-xl font-bold text-blue-900">
      {title}
      {tag && (
        <span className="ml-2.5 align-middle text-xs font-semibold uppercase tracking-wider text-gray-400">
          {tag}
        </span>
      )}
    </h2>
    {lede && <p className="mt-1.5 mb-4 max-w-3xl text-[15px] text-gray-600">{lede}</p>}
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/* Page data                                                           */
/* ------------------------------------------------------------------ */

const YEALINK_DESK = {
  columns: ['Model', 'Best for', 'Lines / display', 'Wireless'],
  rows: [
    ['T31G', 'Break room, warehouse, shared desk', '2 lines · 2.3" mono', '—'],
    [<>T33G <Pill tone="pick">value pick</Pill></>, 'Standard staff desk', '4 lines · 2.4" color', '—'],
    ['T34W', 'Desk with no ethernet drop', '4 lines · 2.4" color', 'Wi-Fi · BT optional'],
    ['T43U', 'Heavy phone user, mono screen', '12 accts · 8 keys · 3.7" mono', 'Optional dongle'],
    ['T44W', 'Front desk, color + wireless', '12 accts · 8 keys · 2.8" color', 'Wi-Fi + Bluetooth'],
    ['T46U', 'Manager / power user', '16 accts · 10 keys · 4.3" color', 'Optional dongle'],
    ['T53W', 'Office standard with headset', '12 accts · 8 keys · 3.7" mono', 'Wi-Fi + Bluetooth'],
    [<>T54W <Pill tone="popular">most popular</Pill></>, 'Owner, manager, front office', '16 accts · 10 keys · 4.3" color', 'Wi-Fi + Bluetooth'],
    ['T57W', 'Executive desk', '16 accts · 7" color touchscreen', 'Wi-Fi + Bluetooth'],
    ['T48U', 'Executive, wired only', '16 accts · 7" color touchscreen', 'Optional dongle'],
    ['T58W', 'Reception with video / Android apps', '16 accts · 7" touchscreen + camera', 'Wi-Fi + Bluetooth'],
    ['T73U / T73W / T74U / T74W / T77U', 'Newest generation — ask us', 'Current Yealink T7 range', 'W models: Wi-Fi + BT'],
  ],
};

const GRANDSTREAM_DESK = {
  columns: ['Model', 'Best for', 'Lines / display', 'Wireless'],
  rows: [
    ['GRP2612 / 2612W', 'Entry desk', '4 keys · 2.4" LCD', '2612W: Wi-Fi'],
    ['GRP2613', 'Standard desk', '6 keys · 2.8" LCD', '—'],
    ['GRP2614', 'Desk needing extra BLF keys', '4 keys + second screen', 'Wi-Fi + Bluetooth'],
    ['GRP2615', 'Manager', '10 keys · 4.3" LCD', 'Wi-Fi + Bluetooth'],
    ['GRP2616', 'Front desk / dispatch', '6 keys + second screen · 4.3"', 'Wi-Fi + Bluetooth'],
    ['GRP2624 / 2634 / 2636 / 2650 / 2670', 'Newer GRP models — supported', '8–14 keys, up to 7" touchscreen', 'Wi-Fi + Bluetooth'],
  ],
};

const FANVIL_DESK = {
  columns: ['Model', 'Best for', 'Notes'],
  rows: [
    ['X3U / X3U Pro', 'Low-cost gigabit desk phone', '2.8" color, 6 SIP lines'],
    ['X4U / X5U / X6U', 'Mid to high-end desk', 'Color screens, dual gigabit, PoE'],
    ['X7 / X7C', 'Reception, many BLF keys', 'Large touchscreen, DSS keys'],
    ['V62 / V63 / V64 / V65 / V67', 'Current V-series desk range', 'Business phones with modern firmware'],
    ['H2U / H5', 'Hotel & guest rooms', 'Hospitality keys, wall-mountable'],
    ['W611W', 'Wi-Fi desk phone, no cabling', 'Dual-band Wi-Fi'],
    ['i30', 'Door / gate intercom', 'SIP video intercom with relay'],
  ],
};

const CORDLESS = {
  columns: ['System', 'Best for', 'Capacity'],
  rows: [
    [<>Yealink W73P / W76P <Pill tone="pick">common pick</Pill></>, 'Single-base cordless for a small site', 'Up to 10 handsets on a W70B base'],
    ['Yealink W80 (W80B / W80DM)', 'Multi-cell — warehouse, multi-floor', 'Roaming across many base stations'],
    ['Grandstream DP750 / DP752 + DP720 / DP730', 'Budget cordless', 'Up to 5 handsets per base'],
  ],
};

const CONFERENCE = {
  columns: ['Model', 'Best for', 'Notes'],
  rows: [
    ['Yealink CP925', 'Huddle room, small conference room', 'Wi-Fi, Bluetooth, USB-C'],
    ['Yealink CP965', 'Medium to large boardroom', '5" touchscreen, expansion mics available'],
    ['Yealink CP920 / CP960', 'Previous generation, still supported', 'Fine to keep if you already own them'],
    ['Grandstream GAC2500', 'Existing Grandstream rooms', 'Android conference phone'],
  ],
};

const WIFI_HANDSETS = {
  columns: ['Model', 'Best for', 'Notes'],
  rows: [
    ['Grandstream WP810 / WP820 / WP826', 'Roaming staff on existing Wi-Fi', 'Rugged models available; no DECT base needed'],
    ['Fanvil W611W', 'Wi-Fi desk phone', 'Desk form factor, wireless uplink'],
  ],
};

const ANALOG_LEFT = {
  columns: ['Model', 'Purpose'],
  rows: [
    ['Grandstream HT801 / HT802', '1–2 analog phone or fax ports'],
    ['Grandstream HT812 / HT814 / HT818', '2 / 4 / 8 analog ports'],
    ['Yeastar TA200 / TA400 / TA800', '2 / 4 / 8-port analog gateways'],
    ['Grandstream GXW4104 / GXW4108', 'Analog trunk (FXO) gateways'],
  ],
};

const ANALOG_RIGHT = {
  columns: ['Model', 'Purpose'],
  rows: [
    ['Algo 8301', 'Overhead paging adapter'],
    ['Algo 8180 / 8186 / 8189', 'SIP ringers & ceiling speakers for noisy areas'],
    ['Algo 8196 / 8103', 'Outdoor horn speaker · audio alerter'],
    ['Grandstream GDS3705 / GDS3710', 'Door intercom with entry control'],
    ['Grandstream GSC3516 / GSC3518', 'SIP paging & intercom speakers'],
  ],
};

const SOFTPHONES = {
  columns: ['App', 'Platform', 'Notes'],
  rows: [
    ['Acrobits Groundwire', 'iOS · Android', 'Push notifications, good battery behaviour — our usual recommendation'],
    ['Acrobits Softphone', 'iOS · Android', 'Lighter-weight sibling of Groundwire'],
    ['Zoiper 5', 'Windows · macOS · iOS · Android', 'Free tier available'],
    ['Linphone', 'Desktop & mobile', 'Open source, no licence cost'],
    ['Grandstream Wave / Softphone', 'iOS · Android · Desktop', 'Pairs naturally with Grandstream desk fleets'],
  ],
};

const BRING_YOUR_OWN = {
  columns: ['Brand', 'Models we can take over'],
  rows: [
    ['Poly / Polycom', 'Edge E550 · VVX 150 / 250 / 350 / 450 · VVX 3xx / 4xx / 5xx / 6xx · SoundStation IP 5000 / 6000 / 7000 · CCX 500'],
    ['Yealink (older)', 'T2x series, T4x G/S/P series, T5x S series, W52P / W56P, CP860'],
    ['Grandstream (older)', 'GXP16xx (incl. GXP1625) · GXP17xx · GXP21xx · GXV32xx / 33xx / 34xx video'],
    ['Snom', 'D1xx · D3xx · D7xx series · C520 / C620 conference · M-series DECT · PA1 paging'],
    ['Cisco', '88xx series · 79x1 · SPA5xx series · SPA1xx ATAs'],
    ['Others', 'Avaya J100 series · Mitel/Aastra 53xx & 6xxx · Digium D4x–D7x · Htek UC9xx · Panasonic KX-UT/TGP · Obihai OBi3xx / OBi10xx · Escene · Atcom · Flyingvoice · VTech · Swissvoice · Sangoma S-series · 2N intercoms'],
  ],
};

/* ------------------------------------------------------------------ */
/* Page                                                                 */
/* ------------------------------------------------------------------ */

function SupportedPhones() {
  useDocTitle('Supported Phones & Devices');

  return (
    <div>
      {/* Hero Section */}
      <div className="relative overflow-hidden mt-4 lg:mt-0 h-[320px] md:h-[380px] lg:h-[450px]">
        <div className="absolute inset-0 z-0">
          <img
            src={about}
            alt="Supported Phones Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-black bg-opacity-50 z-10"></div>
        <div className="relative z-20 h-full flex items-center justify-center text-center text-white">
          <h2 className={`${commonStyles.pageTitle} text-white`}>
            Supported Phones &amp; Devices
          </h2>
        </div>
      </div>

      <CommonContainer>
        {/* Intro */}
        <div className="mt-6 ">
            <div className="mt-6 text-right">
            <Link to="/telecommunications" className="text-blue-500 hover:underline text-sm font-semibold">
              &larr; Back to Telecommunications
            </Link>
          </div>
          <p className={`${commonStyles.paragraph} max-w-3xl`}>
            Every device below is zero-touch provisioned by QComm: plug it into your network,
            and it pulls its extension, lines, speed dials, directory and voicemail settings
            from our servers automatically. No manual configuration, no per-phone setup fee.
          </p>
          <p className="mt-3 text-sm text-gray-400">
            Current as of August 2026 &middot; Models listed are the ones we stock, deploy and
            support for new orders.
          </p>
        </div>

        {/* Yealink */}
        <Section
          title="Desk phones — Yealink"
          tag="our primary line"
          lede="What we put on most desks. Gigabit, PoE-powered (one cable), HD audio, and the deepest provisioning support in our platform."
        >
          <SpecTable columns={YEALINK_DESK.columns} rows={YEALINK_DESK.rows} />
          <Note title="Expansion modules.">
            Receptionists who watch many extensions can add a sidecar (EXP43/EXP50) to the
            T43U, T46U, T48U, T53W, T54W or T57W. Busy-lamp keys are provisioned by us, not
            typed in by hand.
          </Note>
        </Section>

        {/* Grandstream */}
        <Section
          title="Desk phones — Grandstream"
          tag="budget & mixed fleets"
          lede="Strong price-per-desk, and the line we most often find already on site when we take over an existing system."
        >
          <SpecTable columns={GRANDSTREAM_DESK.columns} rows={GRANDSTREAM_DESK.rows} />
        </Section>

        {/* Fanvil */}
        <Section
          title="Desk phones — Fanvil"
          tag="specialty & rugged"
          lede="Where the phone has to survive the room: shop floors, hotel rooms, clean rooms, entryways."
        >
          <SpecTable columns={FANVIL_DESK.columns} rows={FANVIL_DESK.rows} />
        </Section>

        {/* Cordless */}
        <Section
          title="Cordless (DECT)"
          lede="For staff who move — shop floor, kitchen, stockroom, clinic. Range comes from the base station, so coverage doesn't depend on your Wi-Fi."
        >
          <SpecTable columns={CORDLESS.columns} rows={CORDLESS.rows} />
        </Section>

        {/* Conference */}
        <Section title="Conference phones">
          <SpecTable columns={CONFERENCE.columns} rows={CONFERENCE.rows} />
        </Section>

        {/* Wi-Fi handsets */}
        <Section title="Wi-Fi & cordless-over-Wi-Fi handsets">
          <SpecTable columns={WIFI_HANDSETS.columns} rows={WIFI_HANDSETS.rows} />
        </Section>

        {/* Analog / paging / door */}
        <Section
          title="Analog adapters, overhead paging & door stations"
          lede="Keeping the fax line, the warehouse horn, the elevator phone or the front gate on the system — these are provisioned the same way the desk phones are."
        >
          <div className="grid md:grid-cols-2 gap-x-10">
            <SpecTable columns={ANALOG_LEFT.columns} rows={ANALOG_LEFT.rows} />
            <SpecTable columns={ANALOG_RIGHT.columns} rows={ANALOG_RIGHT.rows} />
          </div>
        </Section>

        {/* Softphones */}
        <Section
          title="Softphones & mobile"
          lede="Every QComm extension can also ring a computer or a phone in your pocket, at no extra hardware cost."
        >
          <SpecTable columns={SOFTPHONES.columns} rows={SOFTPHONES.rows} />
        </Section>

        {/* Bring your own */}
        <Section
          title="Already own phones?"
          tag="bring your own"
          lede="We can usually adopt an existing fleet rather than replace it. Beyond the models above, QComm also provisions:"
        >
          <SpecTable columns={BRING_YOUR_OWN.columns} rows={BRING_YOUR_OWN.rows} />
          {/* <Note title="Two conditions on bring-your-own.">
            The phone must not be carrier-locked (handsets bought from a previous provider are
            often locked to that provider and cannot be reused), and it has to still be
            receiving firmware updates from its manufacturer. Send us the model and MAC
            address and we'll tell you within a day.
          </Note>
          <Note tone="warn" title="Not on the list?">
            This page shows what we deploy day to day, not the limit of what works. QComm
            speaks standard SIP, so most business-grade phones can be added — several of the
            brands above were added because a customer asked. Ask before you assume a phone is
            unsupported.
          </Note> */}
        </Section>

        {/* Footer CTA */}
        <div className="my-16  border-gray-200 pt-6 text-center">
            <p>QComm by Quadprosoft — Birmingham-metro business phone service</p>
          <p className={`${commonStyles.paragraph} max-w-2xl mx-auto`}>
            Questions on a specific model, an existing fleet, or a quote? Please{' '}
            <Link to="/contact" className="text-blue-500 hover:underline">
              contact us
            </Link>{' '}
            and we'll size it with you.
          </p>
        </div>
      </CommonContainer>
    </div>
  );
}

export default SupportedPhones;
